import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { useOrganizationId } from '../../helpers/auth-helpers/auth.hooks';
import { type FloraAsideChat, useFloraAsideChat } from './hooks/use-flora-aside-chat';

/** Holds the right dock's live conversation for the panels that render it. */
interface FloraAsideChatStore {
  get: () => FloraAsideChat | null;
  set: (chat: FloraAsideChat | null) => void;
  subscribe: (listener: () => void) => () => void;
}

interface FloraAsideSessionContextValue {
  store: FloraAsideChatStore;
  /** Starts the conversation; a no-op once started. */
  start: () => void;
}

const FloraAsideSessionContext = createContext<FloraAsideSessionContextValue | null>(null);

/**
 * Creates the store the session host publishes into.
 *
 * @returns {FloraAsideChatStore} An empty store
 */
const createFloraAsideChatStore = (): FloraAsideChatStore => {
  let chat: FloraAsideChat | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => chat,
    set: (next) => {
      chat = next;
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

/**
 * Runs the right dock's conversation and publishes it to the store. Rendered
 * by the provider rather than by the panel, so the conversation — its socket,
 * a parked question, staged attachments, pending memory refreshes — outlives
 * the panel, which remounts with every page and closes and reopens.
 *
 * @param {{ store: FloraAsideChatStore }} props - The store to publish into
 * @returns {null} Renders nothing
 */
function FloraAsideSessionHost({ store }: { store: FloraAsideChatStore }) {
  const chat = useFloraAsideChat({ isPageView: false });
  // Publish after every render so panels always see the latest state and actions.
  useLayoutEffect(() => {
    store.set(chat);
  });
  // A layout cleanup, so an outgoing host (org switch) clears the store before
  // the incoming host's layout effect publishes, never after it.
  useLayoutEffect(() => () => store.set(null), [store]);
  return null;
}

/**
 * Keeps the right-dock Flora conversation alive across page changes and panel
 * close/reopen. Mount it once, inside the authenticated app shell, above every
 * page that renders the dock. The conversation starts lazily, when a panel
 * first asks for it, so nothing is fetched for users who never open Flora; it
 * restarts fresh when the organization changes.
 *
 * @param {{ children: ReactNode }} props - The app content
 * @returns {JSX.Element} The provider
 *
 * @example
 * <FloraAsideSessionProvider>
 *   <Routes>…</Routes>
 * </FloraAsideSessionProvider>
 */
export function FloraAsideSessionProvider({ children }: { children: ReactNode }) {
  const organizationId = useOrganizationId();
  const [store] = useState(createFloraAsideChatStore);
  const [isStarted, setIsStarted] = useState(false);
  const value = useMemo(() => ({ store, start: () => setIsStarted(true) }), [store]);
  return (
    <FloraAsideSessionContext.Provider value={value}>
      {isStarted && <FloraAsideSessionHost key={organizationId ?? ''} store={store} />}
      {children}
    </FloraAsideSessionContext.Provider>
  );
}

/**
 * Reports whether a {@link FloraAsideSessionProvider} is mounted above, so the
 * dock can fall back to a conversation of its own where there is none (tests,
 * Storybook).
 *
 * @returns {boolean} True inside the provider
 */
export const useHasFloraAsideSession = (): boolean => useContext(FloraAsideSessionContext) !== null;

/**
 * Reads the right dock's persistent conversation, starting it on first use.
 * Must be called inside a {@link FloraAsideSessionProvider}.
 *
 * @returns {FloraAsideChat | null} The conversation, or null for the render before it first publishes
 * @throws {Error} When called outside the provider
 */
export const useFloraAsideSession = (): FloraAsideChat | null => {
  const context = useContext(FloraAsideSessionContext);
  if (!context) {
    throw new Error('useFloraAsideSession must be used within a FloraAsideSessionProvider');
  }
  const { store, start } = context;
  useEffect(() => {
    start();
  }, [start]);
  return useSyncExternalStore(store.subscribe, store.get, store.get);
};
